import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DialogBox, DialogBoxContent } from "../ui/DialogBox";
import SuccessIcon from "../../assets/icons/notification/success.png";
import ErrorIcon from "../../assets/icons/notification/error.png";
import { ReactComponent as LockIcon } from "../../assets/icons/status-popup/lock.svg";
import InfoIcon from "../../assets/icons/notification/info.png";
import WarningIcon from "../../assets/icons/notification/waring.png";
import { PrimaryButton } from "../ui/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  clearStatusPopup,
  statusPopupSelector,
  refreshStatusPopup,
} from "../../services/redux/slices/statusPopupSlice";
import { useNavigate } from "react-router-dom";
import notificationsServices from "../../services/api/service/notificationService";
import { authSelector, clearAuth } from "../../services/redux/slices/authSlice";
import { RootState } from "../../services/redux/store";
import { clearDocumentDiscardModal } from "../../services/redux/slices/documentFormSlice";
import { encrypt } from "../../utils/crypto";
import ButtonLoader from "../../components/ui/ButtonLoader";
const DialogStatusPopup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    title,
    open,
    status,
    message,
    locationPath,
    buttonStatus,
    anotherDeviceAuth,
    locationFunction,
    isFunction,
    buttonConfirm,
    btnFunc,
    isCross,
    submessage
  } = useSelector(statusPopupSelector);
  const { userProfile } = useSelector(authSelector);
  const deviceType = sessionStorage.getItem("deviceType");
  /**
   * Fetches the list of notifications for the current user.
   *
   * This function makes an asynchronous call to the `getNotifications` service
   * with the user's CIF entity. If the call is successful, the notifications
   * are retrieved. If an error occurs during the API call, it is caught and
   * logged to the console.
   *
   * @async
   * @function getNotificationsList
   * @returns {Promise<void>} A promise that resolves when the notifications are fetched.
   * @throws Will log an error message to the console if the API call fails.
   */
  const businessTypeId = useSelector(
    (state: RootState) => state?.auth?.businessType?.id
  );
  const loanCategoryData: any = useSelector(
    (state: RootState) => state?.auth?.loanCategory
  );
  const getNotificationsList = async () => {
    const bodyObj: any = {};
    let cifEntity = userProfile?.cifEntity;
    bodyObj.cifEntity = cifEntity;
    bodyObj.businessType = encrypt(businessTypeId);
    bodyObj.loanCategory = encrypt(
      loanCategoryData?.TermLoan[0]?.loanCategoryId
    );
    try {
      const res = await notificationsServices.getNotifications(bodyObj);
    } catch (error) {
      console.error("get notifications API error::", error);
    }
  };

  /**
   * Handles the closing of the dialog status popup.
   *
   * This function performs different actions based on the conditions:
   * - If `anotherDeviceAuth` is true, it clears the authentication and status popup, and fetches the notifications list.
   * - If `anotherDeviceAuth` is false:
   *   - If `locationFunction` is a function, it calls `locationFunction` and clears the status popup.
   *   - If `locationPath` is defined, it clears the status popup and navigates to `locationPath`.
   *   - If none of the above conditions are met, it simply clears the status popup.
   */
  const handleClose = () => {
    // if (selectedIndex === 2) {
    //   navigate("FlexiTermLoan");
    // }

    if (anotherDeviceAuth) {
      if (window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "LOGOUT_ANOTHERDEVICE_CLOSE" })
        );
      }
      localStorage.removeItem("selectedIndex");
      sessionStorage?.clear();
      dispatch(clearAuth());
      dispatch(clearStatusPopup());
      if(deviceType!=="mobile"){
        window.location.href = "/login";
      }
    } else {
      dispatch(clearDocumentDiscardModal());
      if (locationPath) {
        dispatch(clearStatusPopup());
        navigate(locationPath);
      } else {
        dispatch(clearStatusPopup());
      }
    }
  };

  const handleOkEvent = () => {
    if (anotherDeviceAuth) {
      dispatch(clearAuth());
      dispatch(clearStatusPopup());
      getNotificationsList();
    } else {
      if (isFunction && typeof locationFunction === "function") {
        locationFunction();
        dispatch(clearStatusPopup());
      }
    }
  };

  /**
   * Handles the refresh action for the status popup.
   * Generates a new timestamp value and dispatches an action to refresh the status popup with this new value.
   * Closes the popup after dispatching the action.
   *
   * @function
   * @name onRefresh
   */
  const onRefresh = () => {
    const newValue = Date.now();
    dispatch(refreshStatusPopup(newValue));
    handleClose();
  };

  /**
   * Returns an icon component based on the provided status.
   *
   * @param {string} status - The status string which determines the icon to be returned.
   * @returns {JSX.Element} The corresponding icon component.
   *
   * - "success": Returns the <SuccessIcon /> component.
   * - "error": Returns the <ErrorIcon /> component.
   * - "no access": Returns the <LockIcon /> component.
   * - Any other value: Returns the <InfoIcon /> component.
   */
  const capitalizeFirstLetter = (str: string) => {
    if (!str) return ""; // Handle empty or undefined strings
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const iconBasedOnStatus = (status: string) => {
    if (status === "success")
      return <img src={SuccessIcon} width="24px" alt="test" />;
    else if (status == "error")
      return <img src={ErrorIcon} width="24px" alt="test" />;
    else if (status == "no access") return <LockIcon />;
    else if (status == "warning")
      return <img src={WarningIcon} width="24px" alt="test" />;
    else return <img src={InfoIcon} width="24px" alt="test" />;
  };

  return (
    <DialogBox open={open} aria-hidden={!open} status={status}>
      {status === "hide-icon" ? (
        <></>
      ) : (
        <Box
          display="flex"
          alignItems="center"
          sx={{
            padding: "15px",
            paddingLeft: "20px",
            paddingBottom: "20px",
            color: "black",
            width: "100%",
          }}
        >
          {title ? (
            <>
              {iconBasedOnStatus(status)} &nbsp;{capitalizeFirstLetter(title)}
            </>
          ) : (
            <>
              {iconBasedOnStatus(status)} &nbsp;{capitalizeFirstLetter(status)}
            </>
          )}
        </Box>
      )}
      {status === "hide-icon" ? (
        <></>
      ) : (
        <IconButton
          aria-label="close"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      )}
      <DialogBoxContent>
        <Box
          sx={{
            display: buttonStatus ? "block" : "flex",
            flexDirection: "column",
            justifyContent: "left",
            textAlign: "left",
            paddingLeft: "12px",
            gap: "24px",
            py: status === "hide-icon" ? "10px" : "auto",
          }}
        >
          {status === "hide-icon" ? (
            <Typography
              sx={{
                paddingInline: "8px",
                textAlign: status === "hide-icon" ? "center" : "left",
                fontSize: status === "hide-icon" ? "20px" : "14px",
                fontWeight: status === "hide-icon" ? "600" : "400",
                color: status === "hide-icon" ? "black" : "auto",
              }}
              component="div"
              dangerouslySetInnerHTML={{ __html: message }}
            />
          ) : (
            <Typography
              sx={{
                paddingInline: "8px",
                textAlign: status === "hide-icon" ? "center" : "left",
                fontSize: status === "hide-icon" ? "20px" : "14px",
                fontWeight: status === "hide-icon" ? "600" : "400",
                color: status === "hide-icon" ? "black" : "auto",
              }}
            >
              {message}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: buttonStatus ? "block" : "flex",
            flexDirection: "column",
            justifyContent: "right",
            alignItems: status === "hide-icon" ? "center" : "right",
            float: status === "hide-icon" ? "center" : "right",
            gap: "24px",
            marginTop: "20px",
          }}
        >
          {buttonStatus && (
            <PrimaryButton
              sx={{ minWidth: "60px", m: "10px" }}
              onClick={onRefresh}
            >
              reload
            </PrimaryButton>
          )}
          {isFunction ? (
            <>
              <PrimaryButton
                onClick={() => handleOkEvent()}
                sx={{ minWidth: "60px" }}
              >
                {status == "error" ? "Close" : "Confirm"}
              </PrimaryButton>
            </>
          ) : (
            buttonConfirm===true?
            (<PrimaryButton
              onClick={() => btnFunc()}
              sx={{ minWidth: "60px" }}
              isLoading={true}
            >
              <ButtonLoader
                title={"I Understand"}
                isLoading={false}
              />
            </PrimaryButton>) : 
            (<PrimaryButton
              onClick={() => handleClose()}
              sx={{ width: status === "hide-icon" ? "60px" : "auto" }}
            >
              Close
            </PrimaryButton>)
          )}
        </Box>
      </DialogBoxContent>
    </DialogBox>
  );
};

export default DialogStatusPopup;
